import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PenLine, CalendarDays, Wand2, LayoutGrid, BookOpen, Home,
  FolderHeart, CalendarRange, Library, Star, Clock, MessageCircle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Badge } from "@/components/ui/badge";

const CARDS = [
  { key: "createActivity", to: "/create", icon: PenLine, color: "bg-secondary/15 text-secondary" },
  { key: "planWeek", to: "/weekly-planner", icon: CalendarDays, color: "bg-primary/15 text-primary", pro: true },
  { key: "makeBilingual", to: "/make-bilingual", icon: Wand2, color: "bg-accent/15 text-accent", pro: true },
  { key: "centers", to: "/learning-centers", icon: LayoutGrid, color: "bg-primary/15 text-primary" },
  { key: "book", to: "/book-companion", icon: BookOpen, color: "bg-secondary/15 text-secondary" },
  { key: "family", to: "/family", icon: Home, color: "bg-accent/15 text-accent" },
  { key: "myActivities", to: "/activities", icon: FolderHeart, color: "bg-primary/15 text-primary" },
  { key: "weeklyPlans", to: "/weekly-plans", icon: CalendarRange, color: "bg-secondary/15 text-secondary", pro: true },
  { key: "resources", to: "/resources", icon: Library, color: "bg-accent/15 text-accent" },
];

function ActivityRow({ a }) {
  return (
    <Link to={`/activities/${a.id}`} data-testid={`recent-${a.id}`}
      className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/40 transition-colors">
      <div className="min-w-0">
        <p className="font-semibold truncate">{a.title}</p>
        <p className="text-sm text-muted-foreground truncate">{a.params?.theme} · {a.params?.age_group}</p>
      </div>
      {a.is_favorite && <Star className="h-4 w-4 text-secondary fill-secondary shrink-0" />}
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { hasPro } = useSubscription();
  const { t } = useLang();
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [profile, setProfile] = useState(null);
  const [feedback, setFeedback] = useState([]);

  useEffect(() => {
    api.get("/activities").then(({ data }) => setRecent(data.slice(0, 5))).catch(() => {});
    api.get("/activities", { params: { favorites: true } }).then(({ data }) => setFavorites(data.slice(0, 5))).catch(() => {});
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/feedback").then(({ data }) => setFeedback(data)).catch(() => {});
    api.post("/feedback/mark-read").catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        <div className="space-y-1">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
            {t("dashboard.welcome")}, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-muted-foreground text-lg">{t("dashboard.subtitle")}</p>
        </div>

        {/* Coach feedback */}
        {feedback.length > 0 && (
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2"><MessageCircle className="h-5 w-5 text-accent" /> {t("dashboard.favorites") === "Favoritos" ? "Comentarios del entrenador" : "Coach Feedback"}</h2>
            <div className="space-y-2">
              {feedback.slice(0, 3).map((f) => (
                <div key={f.id} className="rounded-xl border border-accent/30 bg-accent/5 p-4" data-testid={`feedback-${f.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-accent">{f.feedback_type} · {f.coach_name}</p>
                  <p className="text-foreground mt-1">{f.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Classroom summary */}
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { label: t("dashboard.currentClassroom"), value: profile?.classroom_name },
            { label: t("dashboard.ageGroup"), value: profile?.age_group },
            { label: t("dashboard.weeklyTheme"), value: profile?.current_theme },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">{s.label}</p>
              <p className="font-heading font-bold text-lg mt-1">{s.value || t("dashboard.notSet")}</p>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <section className="space-y-4">
          <h2 className="font-heading font-bold text-xl">{t("dashboard.quickActions")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CARDS.map((c, i) => {
              const Icon = c.icon;
              const active = !!c.to;
              return (
                <motion.button
                  key={c.key}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  onClick={() => active && navigate(c.to)}
                  data-testid={`action-${c.key}`}
                  className={`text-left rounded-2xl border border-border bg-card p-5 transition-transform ${active ? "hover:-translate-y-1 hover:border-primary/40 cursor-pointer" : "opacity-70"}`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`h-11 w-11 rounded-xl flex items-center justify-center ${c.color}`}>
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </span>
                    {!active && <Badge variant="secondary" className="rounded-full text-xs bg-muted text-muted-foreground">{t("dashboard.comingSoon")}</Badge>}
                    {active && c.pro && !hasPro && <Badge variant="secondary" className="rounded-full text-xs bg-secondary/15 text-secondary font-semibold">Pro</Badge>}
                  </div>
                  <h3 className="font-heading font-bold text-lg mt-4">{t(`dashboard.cards.${c.key}`)}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t(`dashboard.cards.${c.key}Desc`)}</p>
                </motion.button>
              );
            })}
          </div>
        </section>

        {/* Recent + favorites */}
        <div className="grid lg:grid-cols-2 gap-8">
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> {t("dashboard.recent")}</h2>
            {recent.length ? <div className="space-y-2">{recent.map((a) => <ActivityRow key={a.id} a={a} />)}</div>
              : <p className="text-muted-foreground">{t("dashboard.noRecent")}</p>}
          </section>
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2"><Star className="h-5 w-5 text-secondary" /> {t("dashboard.favorites")}</h2>
            {favorites.length ? <div className="space-y-2">{favorites.map((a) => <ActivityRow key={a.id} a={a} />)}</div>
              : <p className="text-muted-foreground">{t("dashboard.noRecent")}</p>}
          </section>
        </div>
      </main>
    </div>
  );
}
